import { Button, Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type AudiFinancingModuleWithPermissionsSpecsFragment } from '../../../../api/fragments';
import {
    type OnImportedHandler,
    useExcelUploadModal,
} from '../../../../components/audiFinancingSalesPersonCodeMappingImportExport';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import { useSalesForceFormDrawer } from '../SdmModulePage/SalesForces/SaleForcesFormDrawer';
import AudiFinancingModuleAction from './AudiFinancingModuleAction';
import AudiFinancingModuleDetailsInner from './AudiFinancingModuleDetailsInner';
import HelpersContext from './HelpersContext';

export type AudiFinancingModulePageProps = { module: AudiFinancingModuleWithPermissionsSpecsFragment };

export enum HeaderTabs {
    SalesForce = 'salesForce',
    BankIntegrations = 'bankIntegrations',
    Authorization = 'authorization',
    SalesPersonCodeMappings = 'salesPersonCodeMappings',
}

const AudiFinancingModulePage = ({ module }: AudiFinancingModulePageProps) => {
    const { t } = useTranslation(['audiFinancingModuleDetails']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.SalesForce);
    const salesForceDrawer = useSalesForceFormDrawer();

    const onMappingsImportedRef = useRef<OnImportedHandler | null>(null);
    const excelModal = useExcelUploadModal(module.id, onMappingsImportedRef);
    const screens = Grid.useBreakpoint();
    const helpers = useMemo(
        () => ({
            setOnImported: (onImported: OnImportedHandler) => {
                onMappingsImportedRef.current = onImported;
            },
        }),
        []
    );

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('audiFinancingModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('audiFinancingModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
            <Descriptions.Item label={t('audiFinancingModuleDetails:content.financingModule')}>
                {module.bankModule.displayName}
            </Descriptions.Item>
        </Descriptions>
    );

    // submit button for updating sales peron code mappings
    const footer = [
        activeTab === HeaderTabs.SalesPersonCodeMappings && (
            <Button form="updateSalesPersonCodeMappingForm" htmlType="submit" type="primary">
                {t('audiFinancingModuleDetails:salesPersonCodeMappings.buttons.update')}
            </Button>
        ),
    ].filter(Boolean);

    return (
        <ConsolePageWithHeader
            content={content}
            extra={
                <AudiFinancingModuleAction
                    module={module}
                    openImportMappingsModal={excelModal.open}
                    openSaleForceDrawer={salesForceDrawer.open}
                    permissions={permissions}
                    setActiveTab={setActiveTab}
                />
            }
            footer={footer}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}>
                        <Tabs.TabPane
                            key={HeaderTabs.SalesForce}
                            tab={t('audiFinancingModuleDetails:tabs.salesForce.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.Authorization}
                            tab={t('audiFinancingModuleDetails:tabs.authorization.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.SalesPersonCodeMappings}
                            tab={t('audiFinancingModuleDetails:tabs.salesPersonCodeMappings.title')}
                        />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('audiFinancingModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <HelpersContext.Provider value={helpers}>
                <AudiFinancingModuleDetailsInner activeTab={activeTab} module={module} />
            </HelpersContext.Provider>
            {salesForceDrawer.render(module)}
            {excelModal.render()}
        </ConsolePageWithHeader>
    );
};

export default AudiFinancingModulePage;
